import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import Navbar from '../components/layout/Navbar';
import StockTicker from '../components/layout/StockTicker';
import Footer from '../components/layout/Footer';
import { ArticleCardSidebar } from '../components/cards/ArticleCard';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar, Eye, User, ChevronLeft, Share2, Facebook, Twitter, Linkedin, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryColors = {
  'news': '#3B82F6',
  'personal-finance': '#10B981',
  'debt-management': '#EF4444',
  'small-business': '#8B5CF6',
  'investing': '#F59E0B',
  'markets': '#0A192F',
};

const defaultImage = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=80';

export default function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    fetchArticle();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setReadingProgress(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/articles/${id}`);
      setArticle(response.data);

      const relatedRes = await axios.get(`${API}/articles`, {
        params: { category: response.data.category, limit: 6 }
      });
      setRelatedArticles(relatedRes.data.filter((a) => a.id !== id).slice(0, 5));
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const shareOnSocial = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(article?.title || '');
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`,
    };
    window.open(links[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <StockTicker />
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
          <Skeleton className="w-full h-[400px] rounded-none mb-8" />
          <Skeleton className="w-3/4 h-12 mb-4" />
          <Skeleton className="w-1/2 h-6 mb-8" />
          <Skeleton className="w-full h-[600px]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <StockTicker />
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center">
          <h1 className="text-2xl text-[#0A192F] mb-4" style={{ fontFamily: "'Libre Baskerville', serif" }}>Article Not Found</h1>
          <Link to="/" className="text-[#C5A059] hover:underline">
            Return to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="article-page">
      {/* Reading Progress Bar */}
      <div
        className="fixed top-0 left-0 h-[2px] reading-progress z-50 transition-all duration-150"
        style={{ width: `${readingProgress}%` }}
      />

      <StockTicker />
      <Navbar />

      {/* Article Header */}
      <header className="py-8 sm:py-12 border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <Link
            to={article.subcategory
              ? `/category/${article.category}/${article.subcategory}`
              : `/category/${article.category}`}
            className="inline-flex items-center text-sm text-[#64748B] hover:text-[#C5A059] transition-colors mb-4"
            data-testid="back-to-category"
          >
            <ChevronLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />
            Back to {article.category?.replace('-', ' ')}
          </Link>

          <span
            className="inline-block text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-none mb-4"
            style={{
              backgroundColor: `${categoryColors[article.category] || '#0A192F'}08`,
              color: categoryColors[article.category] || '#0A192F',
              border: `1px solid ${categoryColors[article.category] || '#0A192F'}20`,
            }}
          >
            {article.category?.replace('-', ' ')}
          </span>

          <h1
            className="text-3xl md:text-4xl lg:text-5xl text-[#0A192F] mb-6 leading-tight tracking-tight"
            style={{ fontFamily: "'Libre Baskerville', serif" }}
            data-testid="article-title"
          >
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-[#64748B] font-mono">
            {article.author && (
              <span className="flex items-center">
                <User className="w-4 h-4 mr-1" strokeWidth={1.5} />
                {article.author}
              </span>
            )}
            <span className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" strokeWidth={1.5} />
              {formatDate(article.published_at)}
            </span>
            <span className="flex items-center">
              <Eye className="w-4 h-4 mr-1" strokeWidth={1.5} />
              {article.view_count || 0} views
            </span>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-8">
              {/* Featured Image */}
              {article.image_url && (
                <div className="aspect-video overflow-hidden rounded-none mb-8">
                  <img
                    src={article.image_url || defaultImage}
                    alt={article.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = defaultImage; }}
                  />
                </div>
              )}

              {/* Article Body */}
              <div
                className="prose prose-lg max-w-none"
                data-testid="article-content"
              >
                {article.excerpt && (
                  <p
                    className="text-xl text-[#64748B] leading-relaxed mb-8 gold-border-left pl-4"
                    style={{ fontFamily: "'Libre Baskerville', serif" }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.excerpt) }}
                  />
                )}
                {article.content ? (
                  <div
                    className="text-[#1A1A1A]/80 leading-relaxed space-y-4"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
                  />
                ) : (
                  <p className="text-[#64748B]" style={{ fontFamily: "'Outfit', sans-serif" }}>Full article content is not available.</p>
                )}
              </div>

              {/* Share Section */}
              <div className="mt-12 pt-8 border-t border-[#E2E8F0]">
                <h4 className="text-xs font-semibold text-[#64748B] uppercase tracking-widest mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Share this article
                </h4>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => shareOnSocial('facebook')}
                    className="p-3 bg-[#0A192F]/5 text-[#0A192F]/60 hover:bg-[#C5A059]/10 hover:text-[#C5A059] rounded-none transition-colors"
                    data-testid="share-facebook"
                  >
                    <Facebook className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => shareOnSocial('twitter')}
                    className="p-3 bg-[#0A192F]/5 text-[#0A192F]/60 hover:bg-[#C5A059]/10 hover:text-[#C5A059] rounded-none transition-colors"
                    data-testid="share-twitter"
                  >
                    <Twitter className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => shareOnSocial('linkedin')}
                    className="p-3 bg-[#0A192F]/5 text-[#0A192F]/60 hover:bg-[#C5A059]/10 hover:text-[#C5A059] rounded-none transition-colors"
                    data-testid="share-linkedin"
                  >
                    <Linkedin className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={copyLink}
                    className="p-3 bg-[#0A192F]/5 text-[#0A192F]/60 hover:bg-[#C5A059]/10 hover:text-[#C5A059] rounded-none transition-colors"
                    data-testid="copy-link"
                  >
                    <Link2 className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="sticky top-24 space-y-8">
                {/* Related Articles */}
                <div className="sidebar-widget p-6 rounded-none">
                  <h3
                    className="text-lg text-[#0A192F] mb-4"
                    style={{ fontFamily: "'Libre Baskerville', serif" }}
                  >
                    Related Articles
                  </h3>
                  <div className="space-y-4">
                    {relatedArticles.length > 0 ? (
                      relatedArticles.map((related) => (
                        <ArticleCardSidebar key={related.id} article={related} />
                      ))
                    ) : (
                      <p className="text-[#64748B] text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>No related articles found.</p>
                    )}
                  </div>
                </div>

                {/* Share Widget */}
                <div className="sidebar-widget p-6 rounded-none">
                  <h3 className="text-lg text-[#0A192F] mb-4 flex items-center" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                    <Share2 className="w-5 h-5 mr-2 text-[#C5A059]" strokeWidth={1.5} />
                    Share
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => shareOnSocial('facebook')}
                      className="flex items-center justify-center p-2 bg-[#0A192F]/5 text-[#0A192F]/60 hover:bg-[#C5A059]/10 hover:text-[#C5A059] rounded-none text-sm transition-colors"
                    >
                      <Facebook className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Facebook
                    </button>
                    <button
                      onClick={() => shareOnSocial('twitter')}
                      className="flex items-center justify-center p-2 bg-[#0A192F]/5 text-[#0A192F]/60 hover:bg-[#C5A059]/10 hover:text-[#C5A059] rounded-none text-sm transition-colors"
                    >
                      <Twitter className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Twitter
                    </button>
                    <button
                      onClick={() => shareOnSocial('linkedin')}
                      className="flex items-center justify-center p-2 bg-[#0A192F]/5 text-[#0A192F]/60 hover:bg-[#C5A059]/10 hover:text-[#C5A059] rounded-none text-sm transition-colors"
                    >
                      <Linkedin className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      LinkedIn
                    </button>
                    <button
                      onClick={copyLink}
                      className="flex items-center justify-center p-2 bg-[#0A192F]/5 text-[#0A192F]/60 hover:bg-[#C5A059]/10 hover:text-[#C5A059] rounded-none text-sm transition-colors"
                    >
                      <Link2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Copy Link
                    </button>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
